/* ════ COSTS DASHBOARD ═════════════════════════════════════ */
import { state } from './state.js';
import { $, $$ } from './dom.js';
import { fdate } from './utils/date.js';
import { gbp } from './utils/money.js';
import { canSeeTask } from './privacy.js';
import { showToast } from './utils/toast.js';

const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const resName = id => state.resources.find(r => r.id === id)?.name || id || '—';

// Tasks with a cost that fall within [from, to] for the chosen person.
function rangeTasks() {
  const from = $('cd-from').value, to = $('cd-to').value, person = $('cd-person').value;
  return state.tasks.filter(t => {
    if (!(Number(t.cost) > 0)) return false;
    if (person !== 'all' && t.resource !== person) return false;
    if (from && t.end < from) return false;       // ended before the window
    if (to && t.start > to) return false;          // starts after the window
    return true;
  });
}

function populatePerson() {
  const sel = $('cd-person'); const cur = sel.value || 'all';
  sel.innerHTML = '<option value="all">Everyone</option>' +
    state.resources.filter(r => r.id !== 'unassigned')
      .map(r => `<option value="${r.id}">${esc(r.name)}</option>`).join('');
  sel.value = state.resources.find(r => r.id === cur) ? cur : 'all';
}

// Quick date presets relative to today.
function applyPreset(range) {
  const now = new Date(); const y = now.getFullYear(), m = now.getMonth();
  let from = '', to = '';
  if (range === 'month') { from = fdate(new Date(y, m, 1)); to = fdate(new Date(y, m + 1, 0)); }
  else if (range === 'quarter') { const q = Math.floor(m / 3) * 3; from = fdate(new Date(y, q, 1)); to = fdate(new Date(y, q + 3, 0)); }
  else if (range === 'year') { from = fdate(new Date(y, 0, 1)); to = fdate(new Date(y, 11, 31)); }
  // 'all' leaves both blank.
  $('cd-from').value = from; $('cd-to').value = to;
  renderCostDash();
}

export function renderCostDash() {
  if (!$('cd-body')) return;
  populatePerson();
  const tasks = rangeTasks();
  const total = tasks.reduce((s, t) => s + (Number(t.cost) || 0), 0);
  const from = $('cd-from').value, to = $('cd-to').value;
  $('cd-total').textContent = gbp(total);
  $('cd-sub').textContent = tasks.length ? `${tasks.length} task${tasks.length === 1 ? '' : 's'} with a cost` : 'No costs in range';
  $('cd-range-lbl').textContent = from || to ? `${from || '…'} → ${to || '…'}` : 'All time';

  const body = $('cd-body');
  if (!tasks.length) { body.innerHTML = '<div class="list-empty">No costed tasks in this range.</div>'; return; }

  if ($('cd-person').value === 'all') {
    // Per-person breakdown with proportional bars.
    const map = new Map();
    tasks.forEach(t => { const g = map.get(t.resource) || { id: t.resource, total: 0, n: 0 }; g.total += Number(t.cost) || 0; g.n++; map.set(t.resource, g); });
    const groups = [...map.values()].sort((a, b) => b.total - a.total);
    const max = groups[0].total || 1;
    body.innerHTML = `<div class="cd-section-h">By person</div>` + groups.map(g => `
      <div class="cd-bar-row">
        <div class="cd-bar-name">${esc(resName(g.id))}<span class="cd-bar-n">${g.n} task${g.n === 1 ? '' : 's'}</span></div>
        <div class="cd-bar-track"><div class="cd-bar-fill" style="width:${Math.max(2, (g.total / max) * 100)}%"></div></div>
        <div class="cd-bar-val">${gbp(g.total)}</div>
      </div>`).join('');
  } else {
    // Single person: their costed tasks, newest first.
    const rows = tasks.slice().sort((a, b) => b.start.localeCompare(a.start));
    body.innerHTML = `<div class="cd-section-h">${esc(resName($('cd-person').value))} — tasks</div>` + rows.map(t => {
      const seen = canSeeTask(t);
      const label = seen ? `${t.client ? '<b>' + esc(t.client) + '</b> — ' : ''}${esc(t.name)}` : '🔒 Private';
      return `<div class="cd-task-row">
        <span class="cd-task-dates">${t.start} → ${t.end}</span>
        <span class="cd-task-name">${label}</span>
        <span class="cd-task-val">${gbp(t.cost)}</span>
      </div>`;
    }).join('');
  }
}

function exportCostDash() {
  const tasks = rangeTasks(); if (!tasks.length) { showToast('Nothing to export'); return; }
  const from = $('cd-from').value || 'start', to = $('cd-to').value || 'end';
  const out = [['Person', 'Client', 'Task', 'Start', 'End', 'Cost (£)']];
  tasks.slice().sort((a, b) => a.start.localeCompare(b.start)).forEach(t =>
    out.push([resName(t.resource), t.client || '', canSeeTask(t) ? t.name : 'Private', t.start, t.end, Number(t.cost) || 0]));
  out.push(['', '', '', '', 'TOTAL', tasks.reduce((s, t) => s + (Number(t.cost) || 0), 0)]);
  const csv = out.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
  a.download = `cost-dashboard-${from}_${to}.csv`; a.click();
  showToast('Cost dashboard exported ✓');
}

export function initCostDash() {
  if (!$('cd-body')) return;
  applyPreset('month');   // sensible default
  ['cd-from', 'cd-to', 'cd-person'].forEach(id => $(id).addEventListener('change', renderCostDash));
  $$('.cd-preset').forEach(b => b.addEventListener('click', () => applyPreset(b.dataset.range)));
  $('cd-export').addEventListener('click', exportCostDash);
}
